import { UsageType } from '../constants/usageType';
import { throwOnError } from './utils';

export type OrganizationApplicationStatus =
  | 'submitted'
  | 'reviewing'
  | 'pending'
  | 'approved'
  | 'rejected';

export type OrganizationApplication = {
  id: string;
  template_id: string;
  business_type: 'individual' | 'corporate';
  corporate_number: string | null;
  name: string;
  furigana: string | null;
  representative_name: string | null;
  representative_furigana: string | null;
  representative_birth_date: string | null;
  postal_code: string | null;
  address: string | null;
  tel: string | null;
  email: string | null;
  capital: number | null;
  number_of_employees: number | null;
  referral_source: string | null;
  bank_name: string | null;
  bank_code: string | null;
  bank_branch_name: string | null;
  bank_branch_code: string | null;
  bank_account_type: 'saving' | 'current' | null;
  bank_account: string | null;
  bank_account_holder_name: string | null;
  status: OrganizationApplicationStatus;
  applied_at: string | null;
  approved_at: string | null;
  private_money_ids: string[];
  organization_code: string | null;
  head_office_shop: ProposedShop | null;
  additional_shops: ProposedShop[];
  submitted_by_admin_user: AdminUserInfo | null;
  last_updated_by_admin_user: AdminUserInfo | null;
  created_at: string;
  updated_at: string;
};

// Embedded parent group in response
export type EmbeddedUserTagGroup = {
  id: string;
  name_en: string;
  name_ja: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  max_selections: number;
  is_active: boolean;
};

// Embedded subgroup (middle layer) in response. Present when the item belongs to a subgroup.
export type EmbeddedUserTagSubgroup = {
  id: string;
  name_en: string;
  name_ja: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  is_active: boolean;
};

// Full tag item with parent group (for response)
export type UserTagGroupItemWithGroup = {
  id: string;
  name_en: string;
  name_ja: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  is_active: boolean;
  user_tag_group: EmbeddedUserTagGroup;
  // 所属 subgroup。group 直下（未分類）の item は null。
  user_tag_subgroup: EmbeddedUserTagSubgroup | null;
};

// Simple tag item type for backward compatibility
export type UserTagGroupItem = {
  id: string;
  name_ja: string;
  name_en: string;
};

export type UserTagGroup = {
  id: string;
  name_ja: string;
  name_en: string;
  items: UserTagGroupItem[];
};

// Response type for proposed shop (uses user_tag_group_items)
export type ProposedShop = {
  shop_name: string;
  shop_name_furigana: string | null;
  postal_code: string | null;
  address: string | null;
  tel: string | null;
  email: string | null;
  contact_person_name: string | null;
  contact_person_name_furigana: string | null;
  user_tag_group_items: UserTagGroupItemWithGroup[];
  // 店舗形態（per-shop）。未設定は null。
  usage_type: UsageType | null;
};

// Input type for proposed shop (uses user_tag_group_item_ids)
export type ProposedShopInput = {
  shop_name: string;
  shop_name_furigana?: string;
  postal_code?: string;
  address?: string;
  tel?: string;
  email?: string;
  contact_person_name?: string;
  contact_person_name_furigana?: string;
  user_tag_group_item_ids: string[];
  // 店舗形態（フロントで必須）。未編集で据え置く場合のみ null を送る。
  usage_type?: UsageType | null;
};

type AdminUserInfo = {
  id: string;
  name: string;
};

type Paginated<T> = {
  rows: T[];
  count: number;
  pagination: {
    current: number;
    has_next: boolean;
    has_prev: boolean;
    max_page: number;
    per_page: number;
  };
};

type GetOrganizationApplicationsResponse = Paginated<OrganizationApplication>;

export const getOrganizationApplications = (query = '') =>
  fetch(`/api/organization-applications?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<GetOrganizationApplicationsResponse>);

export const getOrganizationApplication = (id: string) =>
  fetch(`/api/organization-applications/${id}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<OrganizationApplication>);

type FetchCSVResponse = { url: string };

export const fetchOrganizationApplicationsCSV = (query = '') =>
  fetch(`/api/organization-applications/csv?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<FetchCSVResponse>);

export const approveOrganizationApplication = (id: string) =>
  fetch(`/api/organization-applications/${id}/approve`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then(throwOnError<OrganizationApplication>);

export const rejectOrganizationApplication = (id: string) =>
  fetch(`/api/organization-applications/${id}/reject`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then(throwOnError<OrganizationApplication>);

// submitted / pending の申請を審査中(reviewing)に遷移させる
export const reviewOrganizationApplication = (id: string) =>
  fetch(`/api/organization-applications/${id}/review`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then(throwOnError<OrganizationApplication>);

// submitted / reviewing の申請を保留(pending)に遷移させる
export const holdOrganizationApplication = (id: string) =>
  fetch(`/api/organization-applications/${id}/hold`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then(throwOnError<OrganizationApplication>);

// pending の申請を保留解除(submitted に復帰)する
export const unholdOrganizationApplication = (id: string) =>
  fetch(`/api/organization-applications/${id}/unhold`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then(throwOnError<OrganizationApplication>);

export type UpdateOrganizationApplicationParams = {
  name?: string;
  furigana?: string;
  representative_name?: string;
  representative_furigana?: string;
  representative_birth_date?: string;
  postal_code?: string;
  address?: string;
  tel?: string;
  email?: string;
  capital?: number;
  number_of_employees?: number;
  referral_source?: string;
  bank_name?: string;
  bank_code?: string;
  bank_branch_name?: string;
  bank_branch_code?: string;
  bank_account_type?: 'saving' | 'current';
  bank_account?: string;
  bank_account_holder_name?: string;
  head_office_shop?: ProposedShopInput;
  additional_shops?: ProposedShopInput[];
};

export const updateOrganizationApplication = (
  id: string,
  params: UpdateOrganizationApplicationParams
) =>
  fetch(`/api/organization-applications/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token, ...params }),
  }).then(throwOnError<OrganizationApplication>);
